import * as React from "react";
import type { CilDocumentLink } from "@hp/types";
import { HoverHint } from "../HoverHint";

type LinksValue = { links: CilDocumentLink[]; parties: string[]; open: ((link: CilDocumentLink) => void) | null };

const LinksContext = React.createContext<LinksValue>({ links: [], parties: [], open: null });

/**
 * Makes the rulings a changed event names ("APTEL Appeal 113/2022 of 28 Jul
 * 2025") clickable inside `children`, and shows party names ("M. B. Power
 * (Madhya Pradesh) Limited") in dark blue. `links` comes from the API (only
 * rulings whose order is stored); `onOpen` opens the document. Any of them
 * can be left out to keep the one from an outer provider.
 */
export function DocumentLinks({
  links,
  parties,
  onOpen,
  children,
}: {
  links?: CilDocumentLink[];
  parties?: string[];
  onOpen?: (link: CilDocumentLink) => void;
  children: React.ReactNode;
}) {
  const parent = React.useContext(LinksContext);
  const value = React.useMemo<LinksValue>(
    () => ({ links: links ?? parent.links, parties: parties ?? parent.parties, open: onOpen ?? parent.open }),
    [links, parties, onOpen, parent],
  );
  return <LinksContext.Provider value={value}>{children}</LinksContext.Provider>;
}

function escape(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function DocumentLinkButton({ link, children }: { link: CilDocumentLink; children?: React.ReactNode }) {
  const { open } = React.useContext(LinksContext);
  if (!open) return <>{children ?? link.text}</>;
  const where = link.page ? `, page ${link.page}` : "";
  return (
    <HoverHint hint={`Open this order: ${link.file_name ?? "stored document"}${where}.`}>
      <button
        type="button"
        className="inline text-left font-medium text-[var(--color-accent-content)] underline decoration-dotted underline-offset-2 hover:decoration-solid"
        onClick={(e) => {
          e.stopPropagation();
          open(link);
        }}
      >
        {children ?? link.text}
      </button>
    </HoverHint>
  );
}

/** A party's name, in dark blue so who a decision is for stands out. */
export function PartyName({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold text-blue-900">{children}</span>;
}

type Part = string | { link: CilDocumentLink } | { party: string };

/** `text` with every ruling name that has a stored order turned into a
 * link, and every party name in dark blue. */
export function LinkedText({ text }: { text: string }) {
  const { links, parties } = React.useContext(LinksContext);
  const parts = React.useMemo<Part[]>(() => {
    const byText = new Map(links.map((l) => [l.text, l]));
    const names = new Set(parties.filter((p) => p.trim().length > 2));
    const keys = [...new Set([...byText.keys(), ...names])];
    if (!keys.length || !text) return [text];
    // Longest first, so "APTEL Appeal 113/2022 of 28 Jul 2025" wins over "APTEL Appeal 113/2022".
    const pattern = new RegExp(`(${keys.sort((a, b) => b.length - a.length).map(escape).join("|")})`, "g");
    return text.split(pattern).map((part, i): Part => {
      if (i % 2 === 0) return part;
      const link = byText.get(part);
      return link ? { link } : { party: part };
    });
  }, [links, parties, text]);
  return (
    <>
      {parts.map((part, i) =>
        typeof part === "string" ? (
          <React.Fragment key={i}>{part}</React.Fragment>
        ) : "link" in part ? (
          <DocumentLinkButton key={i} link={part.link} />
        ) : (
          <PartyName key={i}>{part.party}</PartyName>
        ),
      )}
    </>
  );
}
